import { useEffect, useMemo, useRef, useState } from "react";
import {
  BagIcon,
  BanIcon,
  BellIcon,
  CalendarIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CloseIcon,
  CollapseIcon,
  ExpandIcon,
  FlameIcon,
  ReceiptIcon,
  RefreshIcon,
  SpeakerOffIcon,
  SpeakerOnIcon,
  WithIcon
} from "../icons.js";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api.js";
import { formatMoney } from "../format.js";
import {
  actionsFor,
  groupOrders,
  isOverdue,
  itemCount,
  minutesSince,
  isActive,
  modifierLabel,
  pickupLabel,
  stateLabel,
  type Order,
  type OrderState
} from "../orders-model.js";
import {
  acknowledge,
  defaultSoundEnabled,
  initialAlarmState,
  loadSoundEnabled,
  observeOrders,
  pendingIds,
  saveSoundEnabled,
  soundStatus,
  soundStatusLabel
} from "../alarm-model.js";
import { createAlarm } from "../alarm.js";
import {
  applyKitchenClass,
  exitFullscreen,
  loadKitchenMode,
  releaseWakeLock,
  requestFullscreen,
  requestWakeLock,
  saveKitchenMode,
  type WakeLockSentinelLike
} from "../kitchen-mode.js";
import { connectionStatus, isConnectionHealthy } from "../connection-model.js";
import {
  dayWindow,
  daysAgo,
  pluralize,
  shiftDay,
  todayInput,
  usePageTitle
} from "../ui.js";

/**
 * The order queue — what the kitchen works from.
 *
 * Polls rather than pushes: a restaurant leaves this open all shift and a missed order is
 * a lost customer, so a 15s refetch is the simple thing that cannot silently break. If
 * that proves too slow in practice, the fix is a push channel, not a shorter interval.
 */
const POLL_MS = 15_000;

export function OrdersPage() {
  usePageTitle("Orders");
  const { restaurantId = "" } = useParams();
  const qc = useQueryClient();
  const key = ["orders", restaurantId];
  const [showCompleted, setShowCompleted] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  // One day at a time. Unbounded here meant re-downloading the entire order history every 15
  // seconds for as long as the tab stayed open on a till.
  const [day, setDay] = useState(todayInput());
  const isToday = day === todayInput();
  // NOT named `window`: a local of that name shadows the global, so anything reaching for a browser
  // API inside this component silently gets a date range instead.
  const dayRange = dayWindow(day);

  const orders = useQuery({
    queryKey: [...key, day],
    queryFn: () => api.listOrders(restaurantId, dayRange),
    // Only the live day needs polling; history doesn't change.
    ...(isToday ? { refetchInterval: POLL_MS } : {})
  });

  /**
   * Orders still being worked from earlier days.
   *
   * Without this, bounding the queue to one day would STRAND them: an order left PREPARING
   * overnight would silently vanish from the only screen anyone looks at. Fetched over a wider
   * window and shown alongside today, never for a historical day.
   */
  const stragglers = useQuery({
    queryKey: [...key, "stragglers"],
    queryFn: () => api.listOrders(restaurantId, { since: daysAgo(7), until: dayRange.since }),
    enabled: isToday,
    ...(isToday ? { refetchInterval: POLL_MS } : {})
  });

  // --- new-order alarm -------------------------------------------------------
  // Driven by the same 15s poll as the queue: no push, no socket, nothing new to run. The alarm is
  // an attention aid, not the delivery guarantee — that is server-side escalation.
  const alarmRef = useRef<ReturnType<typeof createAlarm>>();
  if (!alarmRef.current) alarmRef.current = createAlarm();
  // A touch device is a kitchen tablet, so sound starts on there and off on a laptop. A saved choice
  // still wins, so deliberately silencing a tablet sticks.
  const coarsePointer =
    typeof matchMedia === "function" ? matchMedia("(pointer: coarse)").matches : false;
  const [soundEnabled, setSoundEnabled] = useState(() =>
    loadSoundEnabled(localStorage, defaultSoundEnabled(coarsePointer))
  );
  const [armed, setArmed] = useState(false);
  const [alarm, setAlarm] = useState(initialAlarmState);

  useEffect(() => () => alarmRef.current?.dispose(), []);

  /**
   * Arm the audio on the FIRST tap anywhere, not just on the sound toggle.
   *
   * Defaulting the preference on cannot make a sound: browsers demand a user gesture, so a tablet left
   * on the queue page would show "Tap to enable sound" and stay silent — the exact way an order gets
   * missed. Staff touch a tab or a card within seconds of picking the tablet up, and that gesture is
   * enough. One-shot, and only while sound is wanted.
   */
  useEffect(() => {
    if (!soundEnabled || armed) return;
    const arm = () => {
      void alarmRef.current?.arm().then((ok) => setArmed(ok));
    };
    window.addEventListener("pointerdown", arm, { once: true });
    return () => window.removeEventListener("pointerdown", arm);
  }, [soundEnabled, armed]);

  const pending = useMemo(
    () => pendingIds(orders.data?.orders ?? []),
    [orders.data]
  );

  // Only today can produce a new order; browsing history must never set the alarm off.
  useEffect(() => {
    if (!isToday || !orders.data) return;
    setAlarm((prev) => observeOrders(prev, pending));
  }, [pending.join(","), isToday, Boolean(orders.data)]);

  useEffect(() => {
    const a = alarmRef.current;
    if (!a) return;
    if (alarm.ringing && soundEnabled && armed) a.start();
    else a.stop();
    // alarm.rings is a dependency so a NEW arrival restarts the three chimes even though `ringing` was
    // already true — otherwise the second order of a rush would arrive in silence.
  }, [alarm.ringing, alarm.rings, soundEnabled, armed]);

  const status = soundStatus(soundEnabled, armed);

  // --- kitchen mode ----------------------------------------------------------
  const [kitchen, setKitchen] = useState(() => loadKitchenMode(localStorage));
  useEffect(() => {
    applyKitchenClass(document.body, kitchen);
    // Leaving the page must not leave the rest of the console hidden.
    return () => applyKitchenClass(document.body, false);
  }, [kitchen]);

  /**
   * The wake lock is re-acquired whenever the page becomes visible again, because the browser DROPS it
   * every time the page is hidden. One request does not last a shift.
   */
  const wakeRef = useRef<WakeLockSentinelLike>();
  useEffect(() => {
    if (!kitchen) {
      void releaseWakeLock(wakeRef.current);
      wakeRef.current = undefined;
      return;
    }
    let cancelled = false;
    const acquire = async () => {
      if (document.visibilityState !== "visible") return;
      const lock = await requestWakeLock(navigator);
      if (cancelled) void releaseWakeLock(lock);
      else wakeRef.current = lock;
    };
    void acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", acquire);
      void releaseWakeLock(wakeRef.current);
      wakeRef.current = undefined;
    };
  }, [kitchen]);

  /**
   * Entering is a user gesture, so it does everything that needs one at once: hides all but the queue,
   * asks for fullscreen, keeps the screen awake, and TURNS SOUND ON — not merely arms it if already
   * enabled. Somebody putting a tablet into kitchen mode wants to hear new orders; making them find a
   * second switch is how an order gets missed.
   */
  const toggleKitchen = async () => {
    const next = !kitchen;
    setKitchen(next);
    saveKitchenMode(localStorage, next);
    if (next) {
      void requestFullscreen(document.documentElement);
      setSoundEnabled(true);
      saveSoundEnabled(localStorage, true);
      const ok = await alarmRef.current!.arm();
      setArmed(ok);
      if (ok) alarmRef.current!.test();
    } else {
      void exitFullscreen(document);
    }
  };

  /** The click IS the gesture that unlocks audio, so arming and enabling are the same action. */
  const toggleSound = async () => {
    const a = alarmRef.current;
    if (!a) return;
    if (soundEnabled && armed) {
      a.stop();
      setSoundEnabled(false);
      saveSoundEnabled(localStorage, false);
      return;
    }
    const ok = await a.arm();
    setArmed(ok);
    setSoundEnabled(ok);
    saveSoundEnabled(localStorage, ok);
    // Prove it out loud. A silent "Sound on" is the failure this whole control exists to avoid.
    if (ok) a.test();
  };

  // The badge has to age between polls, so it needs its own tick. Without it a frozen page would
  // keep claiming "updated moments ago" forever — which is exactly the lie being fixed.
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 5_000);
    return () => clearInterval(t);
  }, []);

  const connection = connectionStatus({
    lastSuccessAtMs: orders.dataUpdatedAt || undefined,
    failureCount: orders.failureCount,
    pollMs: POLL_MS,
    nowMs: tick,
    online: typeof navigator === "undefined" ? undefined : navigator.onLine
  });

  const advance = useMutation({
    mutationFn: ({ orderId, status }: { orderId: string; status: OrderState }) =>
      api.updateOrderStatus(restaurantId, orderId, status),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key })
  });

  if (orders.isLoading) return <p className="muted">Loading orders…</p>;
  if (orders.error) {
    const e = orders.error;
    return (
      <p className="error">
        Couldn’t load orders: {e instanceof ApiError ? e.message : String(e)}
      </p>
    );
  }

  const stillActive = (stragglers.data?.orders ?? []).filter((o) => isActive(o.state));
  const { active, completed } = groupOrders([
    ...(orders.data?.orders ?? []),
    // Older but unfinished: merged in so nothing is stranded by the day window.
    ...stillActive
  ]);

  return (
    <section>
      <header className="row spread">
        <h1>
          Orders{" "}
          <span className="muted">
            {isToday ? pluralize(active.length, "active order") : `history · ${day}`}
          </span>
        </h1>
        {/* Icon-only, so it stays out of the way on a tablet by the till. The spin doubles as
            the refreshing indicator that used to be text in the heading. */}
        <div className="queue-tools">
        <div className="day-nav">
          <button
            type="button"
            className="secondary"
            onClick={() => setDay(shiftDay(day, -1))}
            aria-label="Previous day"
          >
            <ChevronLeftIcon size={16} />
          </button>
          <input
            type="date"
            value={day}
            max={todayInput()}
            onChange={(e) => setDay(e.target.value || todayInput())}
            aria-label="Show orders for this day"
          />
          <button
            type="button"
            className="secondary"
            onClick={() => setDay(shiftDay(day, 1))}
            disabled={isToday}
            aria-label="Next day"
          >
            <ChevronRightIcon size={16} />
          </button>
          {!isToday ? (
            <button type="button" className="ghost" onClick={() => setDay(todayInput())}>
              <WithIcon icon={<CalendarIcon size={15} />}>Today</WithIcon>
            </button>
          ) : null}
        </div>
        {/* Icon only: the label is the one thing on this bar that never needs reading twice, and on a
            tablet the words cost width the queue can use. The accessible name carries the meaning. */}
        <button
          type="button"
          className={`icon-button kitchen-toggle${kitchen ? " on" : ""}`}
          onClick={() => void toggleKitchen()}
          aria-pressed={kitchen}
          aria-label={kitchen ? "Leave kitchen mode" : "Kitchen mode: full screen order queue"}
          title={
            kitchen
              ? "Leave kitchen mode"
              : "Kitchen mode — full screen, screen stays awake, sound on"
          }
        >
          {kitchen ? <CollapseIcon /> : <ExpandIcon />}
        </button>
        <button
          type="button"
          className={`sound-toggle status-${status}`}
          onClick={() => void toggleSound()}
          aria-pressed={status === "on"}
          title={
            status === "needs-gesture"
              ? "Sound is switched on but this tab needs a tap before it can play"
              : "New orders sound an alarm until someone acknowledges it"
          }
        >
          {status === "on" ? <SpeakerOnIcon /> : <SpeakerOffIcon />}
          <span>{soundStatusLabel(status)}</span>
        </button>
        <button
          type="button"
          className={`icon-button${orders.isFetching ? " spinning" : ""}`}
          onClick={() => void qc.invalidateQueries({ queryKey: key })}
          disabled={orders.isFetching}
          aria-label="Refresh orders"
          title="Refresh orders"
        >
          <RefreshIcon />
        </button>
        </div>
      </header>

      {/* Connection state. Quiet when healthy, loud when not: an empty queue and a dead connection
          look identical otherwise, and staff trust the screen. */}
      <p className={`conn conn-${connection.state}`} role="status">
        <span className="conn-dot" aria-hidden="true" />
        {connection.label}
        {!isConnectionHealthy(connection) ? (
          <button
            type="button"
            className="ghost"
            onClick={() => void qc.invalidateQueries({ queryKey: key })}
          >
            <WithIcon icon={<RefreshIcon size={15} />}>Retry</WithIcon>
          </button>
        ) : null}
      </p>

      {alarm.ringing ? (
        <div className="alarm-bar" role="status">
          <strong>
            {pluralize(pending.length, "new order")} waiting
          </strong>
          {/* Visual as well as audible: a kitchen is loud, and the sound may be off entirely. */}
          <button type="button" className="primary" onClick={() => setAlarm(acknowledge)}>
            <WithIcon icon={<CheckIcon size={16} />}>Got it</WithIcon>
          </button>
        </div>
      ) : null}

      {advance.error ? (
        <p className="error">
          {advance.error instanceof ApiError ? advance.error.message : String(advance.error)}
        </p>
      ) : null}

      {active.length === 0 ? (
        <p className="muted empty-state">
          <WithIcon icon={<ReceiptIcon size={20} />}>
            {isToday ? "No active orders" : `Nothing active on ${day}`}
          </WithIcon>
        </p>
      ) : (
        <ul className="order-list">
          {active.map((o) => (
            <OrderCard
              key={o.orderId}
              order={o}
              expanded={expanded === o.orderId}
              onToggle={() => setExpanded(expanded === o.orderId ? null : o.orderId)}
              onAction={(status) => advance.mutate({ orderId: o.orderId, status })}
              busy={advance.isPending}
            />
          ))}
        </ul>
      )}

      <button
        type="button"
        className="secondary"
        onClick={() => setShowCompleted(!showCompleted)}
        aria-expanded={showCompleted}
      >
        <WithIcon icon={<CheckCircleIcon size={16} />}>
          {showCompleted ? "Hide" : "Show"} completed ({completed.length})
        </WithIcon>
      </button>

      {showCompleted ? (
        completed.length === 0 ? (
          <p className="muted">Nothing completed yet.</p>
        ) : (
          <ul className="order-list muted-list">
            {completed.map((o) => (
              <OrderCard
                key={o.orderId}
                order={o}
                expanded={expanded === o.orderId}
                onToggle={() => setExpanded(expanded === o.orderId ? null : o.orderId)}
                onAction={() => undefined}
                busy={false}
              />
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}

function OrderCard({
  order,
  expanded,
  onToggle,
  onAction,
  busy
}: {
  order: Order;
  expanded: boolean;
  onToggle: () => void;
  onAction: (status: OrderState) => void;
  busy: boolean;
}) {
  const waited = minutesSince(order.submittedAtIso);
  const late = isOverdue(order);
  const actions = actionsFor(order.state);
  // Which destructive action is armed, if any. Reset whenever the order changes state, so an arm
  // never carries over onto a different action than the one the user aimed at.
  const [armed, setArmed] = useState<OrderState | null>(null);
  useEffect(() => setArmed(null), [order.state]);

  return (
    <li className={`order-card${late ? " overdue" : ""}`}>
      <button type="button" className="order-summary" onClick={onToggle} aria-expanded={expanded}>
        <span className={`badge state-${order.state.toLowerCase()}`}>
          {stateLabel(order.state)}
        </span>
        <span className="order-who">{pickupLabel(order)}</span>
        <span className="muted">
          <WithIcon icon={<BagIcon size={14} />}>
            {itemCount(order)} · {formatMoney(order.total)}
          </WithIcon>
        </span>
        {waited !== undefined ? (
          <span className={late ? "error" : "muted"}>
            <WithIcon icon={<ClockIcon size={14} />}>
              {late ? "late · " : ""}
              {waited}m
            </WithIcon>
          </span>
        ) : null}
      </button>

      {/*
        Active orders show what to cook WITHOUT a tap. The kitchen works from the items, so hiding them
        behind an expand meant opening every card to read the queue — fine for an owner scanning history
        on a laptop, useless on a counter tablet during service. Finished orders stay collapsed: nobody
        needs their contents.
      */}
      {isActive(order.state) || expanded ? (
        <ul className="order-lines-always">
          {order.lines.map((l, i) => (
            <li key={`${l.nameSnapshot}-${i}`}>
              <strong>{l.quantity}×</strong> {l.nameSnapshot}
              {l.modifiers.length > 0 ? (
                <span className="mods"> {l.modifiers.map(modifierLabel).join(", ")}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {expanded ? (
        <div className="order-detail">
          <dl className="order-meta">
            {order.customerPhone ? (
              <>
                <dt>Phone</dt>
                <dd>
                  <a href={`tel:${order.customerPhone}`}>{order.customerPhone}</a>
                </dd>
              </>
            ) : null}
            {order.estimatedReadyMinutes !== undefined ? (
              <>
                <dt>Promised</dt>
                <dd>{order.estimatedReadyMinutes} min at time of call</dd>
              </>
            ) : null}
            <dt>Total</dt>
            <dd>{formatMoney(order.total)}</dd>
            <dt>Order ID</dt>
            <dd className="muted mono">{order.orderId}</dd>
          </dl>
        </div>
      ) : null}

      {actions.length > 0 ? (
        <div className="order-actions">
          {actions.map((a) => (
            <button
              key={a.to}
              type="button"
              className={a.danger ? "danger" : "primary"}
              disabled={busy}
              onClick={() => {
                /**
                 * Two taps for a destructive action, not a dialog.
                 *
                 * A native confirm blocks the whole tab behind browser chrome that gets dismissed
                 * blindly mid-rush, and it is a poor target on a tablet. Arming the button keeps the
                 * decision where the finger already is, and a stray tap cannot reject an order
                 * because the second tap has to be deliberate.
                 *
                 * Not undo: the order state machine has no path out of REJECTED or CANCELLED, so the
                 * guard has to come before the action rather than after it.
                 */
                if (a.danger && armed !== a.to) {
                  setArmed(a.to);
                  return;
                }
                setArmed(null);
                onAction(a.to);
              }}
            >
              <WithIcon icon={<OrderActionIcon to={a.to} />}>
                {armed === a.to ? `${a.label}: tap again` : a.label}
              </WithIcon>
            </button>
          ))}
        </div>
      ) : null}
    </li>
  );
}

/** The icon for moving an order to a state. */
function OrderActionIcon({ to }: { to: OrderState }) {
  if (to === "ACCEPTED") return <CheckIcon size={16} />;
  if (to === "PREPARING") return <FlameIcon size={16} />;
  if (to === "READY") return <BellIcon size={16} />;
  if (to === "COMPLETED") return <BagIcon size={16} />;
  if (to === "REJECTED") return <CloseIcon size={16} />;
  return <BanIcon size={16} />;
}
